// Human-readable record ids: INIT-007, TSK-041, GOL-004, PER-012.
//
// The internal Firestore doc id is still the real key; `ref` is a friendly
// label assigned on create and shown in the UI with a copy button. Numbers
// come from one counter doc PER TENANT (Phase 5B):
// tenants/{tenantId}/counters/refs = { INIT, TSK, GOL, PER, ... } — moved
// off the old global counters/refs so two tenants creating records at the
// same time never contend on the same doc, and so Tenant A's ref numbering
// can never reveal how many records Tenant B has created.

import { doc, runTransaction } from 'firebase/firestore';
import { db } from '../firebase';
import { resolveActiveTenantId } from './tenant';

export type RefKind = 'initiative' | 'task' | 'goal' | 'contact' | 'action';

const PREFIX: Record<RefKind, string> = {
  initiative: 'INIT',
  task: 'TSK',
  goal: 'GOL',
  contact: 'PER',
  action: 'ACT',
};

export function formatRef(kind: RefKind, n: number): string {
  return `${PREFIX[kind]}-${String(n).padStart(3, '0')}`;
}

const countersRef = () => doc(db, 'tenants', resolveActiveTenantId(), 'counters', 'refs');

/** Reserve `count` sequential refs of a kind and return them formatted.
 *  Gaps (from a later failed write) are harmless — these are just labels. */
export async function allocateRefs(kind: RefKind, count = 1): Promise<string[]> {
  if (kind === 'initiative') throw new Error('Initiative references are allocated by server-authorized creation.');
  if (count <= 0) return [];
  const key = PREFIX[kind];
  const start = await runTransaction(db, async (tx) => {
    const snap = await tx.get(countersRef());
    const current: number = (snap.exists() ? (snap.data()?.[key] as number) : 0) ?? 0;
    tx.set(countersRef(), { [key]: current + count }, { merge: true });
    return current + 1;
  });
  return Array.from({ length: count }, (_, i) => formatRef(kind, start + i));
}
